import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Thought,
  getProjectThoughts,
  getThoughtById,
  createThought,
  updateThought,
  deleteThought
} from '../supabase/thoughts';

// Define cache times - using the same aggressive caching as other queries
const HOUR_IN_MS = 1000 * 60 * 60;
const DAY_IN_MS = HOUR_IN_MS * 24;
const STALE_TIME = HOUR_IN_MS * 1; // 1 hour stale time
const GC_TIME = DAY_IN_MS * 7; // 7 days garbage collection

// Define query keys for better cache management
export const thoughtKeys = {
  all: ['thoughts'] as const,
  lists: () => [...thoughtKeys.all, 'list'] as const,
  list: (filters: string) => [...thoughtKeys.lists(), { filters }] as const,
  details: () => [...thoughtKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...thoughtKeys.details(), id.toString()] as const,
};

/**
 * Hook to get all thoughts for a project
 */
export function useProjectThoughts(projectId: string | number | undefined) {
  return useQuery({
    queryKey: thoughtKeys.list(projectId?.toString() || ''),
    queryFn: () => getProjectThoughts(projectId || ''),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: !!projectId,
  });
}

/**
 * Hook to get a thought by ID
 */
export function useThought(thoughtId: string | number | null | undefined) {
  return useQuery({
    queryKey: thoughtKeys.detail(thoughtId || ''),
    queryFn: () => getThoughtById(thoughtId || ''),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: !!thoughtId,
  });
}

/**
 * Hook to create a thought
 */
export function useCreateThought() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      title = null,
      content = '',
      is_favorite = false
    }: {
      projectId: string | number;
      title?: string | null;
      content?: string;
      is_favorite?: boolean;
    }) => createThought(
      projectId,
      title,
      content,
      is_favorite
    ),
    onSuccess: (_, variables) => {
      // Invalidate the project thoughts query to force a refetch
      queryClient.invalidateQueries({
        queryKey: thoughtKeys.list(variables.projectId.toString()),
      });
    },
  });
}

/**
 * Hook to update a thought
 */
export function useUpdateThought() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      thoughtId,
      updates,
      projectId
    }: {
      thoughtId: string | number;
      updates: Partial<Omit<Thought, 'id' | 'created_at' | 'updated_at' | 'tenant_id'>>;
      projectId: string | number;
    }) => updateThought(thoughtId, updates),
    onSuccess: (_, variables) => {
      // Invalidate the specific thought detail
      queryClient.invalidateQueries({
        queryKey: thoughtKeys.detail(variables.thoughtId.toString()),
      });
      // Invalidate the project thoughts list
      queryClient.invalidateQueries({
        queryKey: thoughtKeys.list(variables.projectId.toString()),
      });
    },
  });
}



/**
 * Hook to delete a thought
 */
export function useDeleteThought() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      thoughtId,
      projectId
    }: {
      thoughtId: string | number;
      projectId: string | number;
    }) => deleteThought(thoughtId),
    onSuccess: (_, variables) => {
      // Invalidate the project thoughts list
      queryClient.invalidateQueries({
        queryKey: thoughtKeys.list(variables.projectId.toString()),
      });
    },
  });
}
